import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authClient } from '@/lib/auth';

type AuthMode = 'sign-in' | 'sign-up';

export default function AuthPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>('sign-in');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const title =
    mode === 'sign-in' ? t('auth.title.signIn') : t('auth.title.signUp');

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const callbackURL = `${window.location.origin}/#/`;
      if (mode === 'sign-in') {
        const result = await (authClient.signIn as any).email({
          email: email.trim(),
          password,
          callbackURL,
        });
        if (result?.error) {
          setError(result.error.message || t('auth.errors.signInFailed'));
          return;
        }
      } else {
        if (password !== confirmPassword) {
          setError(t('auth.errors.passwordMismatch'));
          return;
        }
        const result = await (authClient.signUp as any).email({
          name: name.trim(),
          email: email.trim(),
          password,
          invitationCode: invitationCode.trim(),
          callbackURL,
        });
        if (result?.error) {
          setError(result.error.message || t('auth.errors.signUpFailed'));
          return;
        }
      }

      window.location.assign('/#/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errors.authenticationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md items-center px-4">
      <div className="w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-slate-900">{title}</h1>
        <p className="mb-6 text-sm text-slate-500">{t('auth.description')}</p>

        <form className="space-y-3" onSubmit={handleSubmit}>
          {mode === 'sign-up' && (
            <input
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              placeholder={t('auth.fields.name')}
              name="name"
              autoComplete="name"
              value={name}
              onChange={event => setName(event.target.value)}
              required
            />
          )}
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            placeholder={t('auth.fields.email')}
            name="username"
            type="email"
            autoComplete="username"
            value={email}
            onChange={event => setEmail(event.target.value)}
            required
          />
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
            placeholder={t('auth.fields.password')}
            name="password"
            type="password"
            autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
            value={password}
            onChange={event => setPassword(event.target.value)}
            required
          />
          {mode === 'sign-up' && (
            <input
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              placeholder={t('auth.fields.confirmPassword')}
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
              required
            />
          )}
          {mode === 'sign-up' && (
            <input
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
              placeholder={t('auth.fields.invitationCode')}
              name="invitation-code"
              autoComplete="off"
              value={invitationCode}
              onChange={event => setInvitationCode(event.target.value)}
              required
            />
          )}

          {error ? <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div> : null}

          <button
            type="submit"
            className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
            disabled={submitting}
          >
            {submitting
              ? t('auth.buttons.pleaseWait')
              : mode === 'sign-in'
                ? t('auth.buttons.signIn')
                : t('auth.buttons.createAccount')}
          </button>
        </form>

        <button
          type="button"
          className="mt-4 w-full text-center text-xs text-slate-600 hover:text-slate-900"
          onClick={() => {
            setMode(prev => (prev === 'sign-in' ? 'sign-up' : 'sign-in'));
            setError('');
            setConfirmPassword('');
          }}
        >
          {mode === 'sign-in' ? t('auth.links.needAccount') : t('auth.links.alreadyHaveAccount')}
        </button>
      </div>
    </div>
  );
}
